import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedTenant } from '../Shared/ManagedTenant';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminOperation,
    ManagedAdminEditorMode,
    ManagedActionModalMode,
    ManagedAdminActionMode,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    mapTenantAdminModel,
} from '../Shared/ManagedHelpers';
import Administration from '../../Constants/Endpoints/Administration';
import GetTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsRequest';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import AUDTenantRequest from '../../Interfaces/Requests/Administration/AUDTenantRequest';
import {
    AddIcon,
    Button,
    Card,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAIExplainModelResponse from '../../Interfaces/Responses/AI/GetAIExplainModelResponse';
import GetAITenantExplainModelRequest from '../../Interfaces/Requests/AI/GetAITenantExplainModelRequest';

type AUDTenantFormValues = {
    tDetailsName: string;
    tDetailsConnString: string;
    tDetailsLogoImage: string;
    tDetailsNameGeneric: string;
    tDetailsHideFromDemo: boolean;
    tDetailsSQLFirstDayOfWeek: number;
    tDetailsApplicationID: string;
    tDetailsLinkedDBName: string;
    tDetailsURLBase: string;
    tDetailsEncryptKey: string;
    tDetailsEncryptCert: string;
};

type AUDTenantProps = {};

const AUDTenant: React.FC<AUDTenantProps> = () => {
    const [formAUDTenants] = Form.useForm();

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({ open: false, mode: 'info', title: '', message: '', isError: false, });

    const [addButtonClicked, setAddButtonClicked] = useState(true);
    const [updButtonClicked, setUpdButtonClicked] = useState(false);
    const [delButtonClicked, setDelButtonClicked] = useState(false);

    const [tSelectedTenantID, setTSelectedTenantID] = useState(-1);
    const [tSelectedTenantName, setTSelectedTenantName] = useState('');

    const [tIncludeDisabled] = useState(false);

    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);

    const DEFAULT_SQL_FIRST_DAY_OF_WEEK = 7;

    const sqlFirstDayOfWeekOptions = [
        { label: 'Sunday', value: 7 },
        { label: 'Monday', value: 1 },
        { label: 'Tuesday', value: 2 },
        { label: 'Wednesday', value: 3 },
        { label: 'Thursday', value: 4 },
        { label: 'Friday', value: 5 },
        { label: 'Saturday', value: 6 },
    ];

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const isGuid = (value: string): boolean => {
        return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);
    };

    const validateOptionalGuidField = (fieldName: string): Promise<void> => {
        const value = formAUDTenants.getFieldValue(fieldName);

        if (value === undefined || value === null || String(value).trim() === '') {
            return Promise.resolve();
        }

        return isGuid(String(value).trim()) ? Promise.resolve() : Promise.reject();
    };

    const validateRequiredTField = (fieldName: string): Promise<void> => {
        const value = formAUDTenants.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateTDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'tDetailsName':
            case 'tDetailsConnString':
            case 'tDetailsSQLFirstDayOfWeek':
                return validateRequiredTField(rule.field);

            case 'tDetailsLogoImage':
            case 'tDetailsNameGeneric':
            case 'tDetailsLinkedDBName':
            case 'tDetailsURLBase':
            case 'tDetailsEncryptKey':
            case 'tDetailsEncryptCert':
                return Promise.resolve();

            case 'tDetailsApplicationID':
                return validateOptionalGuidField(rule.field);

            default:
                return Promise.reject();
        }
    };

    const setTMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getTOperation = (): ManagedAdminOperation  => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Upd';
        if (delButtonClicked) return 'Del';
        return '';
    };

    const getTActionModalMode = (): ManagedActionModalMode => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        if (delButtonClicked) return 'delete';
        return 'info';
    };

    const getTActionLabel = (): string => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Update';
        if (delButtonClicked) return 'Delete';
        return 'Manage';
    };


    const getTActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const getTActionTargetName = (): string => {
        return tSelectedTenantName || formAUDTenants.getFieldValue('tDetailsName') || '';
    };

    const showTInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const buildTErrorMessage = (prefix: string, err: unknown): string => {
        const apiError = err as ManagedApiError;

        return apiError?.response?.data?.message
            ? `${prefix}: ${apiError.response.data.message}`
            : `${prefix}: ${String(err)}`;
    };

    const showTResultModal = (isError: boolean, message: string) => {
        setManageActionModalState(buildManagedActionModalState(getTActionModalMode(), `${getTActionLabel()} Tenant`, message, isError));
    };

    const clearTSelection = () => {
        setTSelectedTenantID(-1);
        setTSelectedTenantName('');
        setTMode('add');
        setTBlankForm();
    };

    const buildAUDTenantRequest = (): AUDTenantRequest => {
        const tDetailsName = formAUDTenants.getFieldValue('tDetailsName');
        const tDetailsConnString = formAUDTenants.getFieldValue('tDetailsConnString');
        const tDetailsLogoImage = formAUDTenants.getFieldValue('tDetailsLogoImage');
        const tDetailsNameGeneric = formAUDTenants.getFieldValue('tDetailsNameGeneric');
        const tDetailsHideFromDemo = formAUDTenants.getFieldValue('tDetailsHideFromDemo');
        const tDetailsSQLFirstDayOfWeek = Number(formAUDTenants.getFieldValue('tDetailsSQLFirstDayOfWeek'));
        const tDetailsApplicationID = formAUDTenants.getFieldValue('tDetailsApplicationID');
        const tDetailsLinkedDBName = formAUDTenants.getFieldValue('tDetailsLinkedDBName');
        const tDetailsURLBase = formAUDTenants.getFieldValue('tDetailsURLBase');
        const tDetailsEncryptKey = formAUDTenants.getFieldValue('tDetailsEncryptKey');
        const tDetailsEncryptCert = formAUDTenants.getFieldValue('tDetailsEncryptCert');

        return {
            id: tSelectedTenantID,
            name: tDetailsName ?? '',
            connString: tDetailsConnString ?? '',
            logo_Image: tDetailsLogoImage ?? '',
            name_Generic: tDetailsNameGeneric ?? '',
            hideFromDemo: tDetailsHideFromDemo ?? false,
            sql_First_Day_of_Week: Number.isFinite(tDetailsSQLFirstDayOfWeek) ? tDetailsSQLFirstDayOfWeek : DEFAULT_SQL_FIRST_DAY_OF_WEEK,
            applicationId: tDetailsApplicationID && String(tDetailsApplicationID).trim() !== '' ? tDetailsApplicationID : null,
            linked_DB_Name: tDetailsLinkedDBName ?? '',
            url_Base: tDetailsURLBase ?? '',
            encrypt_Key: tDetailsEncryptKey ?? '',
            encrypt_Cert: tDetailsEncryptCert ?? '',
            operation: getTOperation(),
        };
    };

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-tenant-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_TENANT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_TENANT_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManageActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for Tenant Explain.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = 'Tenant Explain';

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase.toLowerCase()) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase.toLowerCase())
        );
    };

    const { mutate: getAIContextAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, explainTenantID, prompt }: GetAITenantExplainModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AITenantExplain`;
                const requestData: GetAITenantExplainModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    explainTenantID: explainTenantID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAIExplainModelResponse, GetAITenantExplainModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_TENANT_AI_CONTEXT_ASSIST: [AI Tenant Explain] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIExplainModelResponse) => {
                setAIContextAssistResultText((response.text ?? '').trim());
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI Tenant Explain returned successfully.' : 'AI Tenant Explain request did not succeed.',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_TENANT_AI_CONTEXT_ASSIST: [AI Tenant Explain] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'Unable to complete the AI Tenant Explain request.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const { data: tenants = [], isLoading: isGetTenantsLoading, refetch: refetchTenants, } = useQuery<GetTenantAdminModelsResponse[], Error, ManagedTenant[]>(
        ['tenantAdminModels', tIncludeDisabled],
        async (): Promise<GetTenantAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS}`;
                const requestData: GetTenantAdminModelsRequest = { gta_includeDisabled: tIncludeDisabled, };
                return await AxiosUtil.get<GetTenantAdminModelsResponse[], GetTenantAdminModelsRequest>(url, requestData);
            } catch (err) {
                console.error('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models AUD] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                console.error('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models AUD] Internal Error: ', err);
                showTInfoModal('Tenant Load Error', buildTErrorMessage('Unable to load Tenants for Manage Tenants.', err));
            },
            select: (response: GetTenantAdminModelsResponse[]): ManagedTenant[] =>
                response
                    .map(mapTenantAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name)),
        }
    );

    const { mutate: audTenant, isLoading: isAUDTenantLoading, reset: resetAUDTenant } = useMutation<boolean, Error, void>(
        async (): Promise<boolean> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.AUD_TENANT_DETAILS_BY_ID}`;
                const requestData: AUDTenantRequest = buildAUDTenantRequest();
                const responseData = await AxiosUtil.post<boolean, AUDTenantRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('AUD_TENANT_DETAILS_BY_ID: [AUD Tenant t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                const tenantName = getTActionTargetName();
                console.error('AUD_TENANT_DETAILS_BY_ID: [AUD Tenant o/e] Internal Error: ', err);
                showTResultModal(true, `${getTActionLabel()} Tenant: ${tenantName} Failed. Please Contact Application Support`);
            },
            onSuccess: async (response: boolean) => {
                const tenantName = getTActionTargetName();
                const actionLabel = getTActionLabel();

                if (delButtonClicked) {
                    clearTSelection();
                    await refetchTenants();
                    showTResultModal(false, `${actionLabel} Tenant: ${tenantName} Succeeded.`);
                    return;
                }

                const refetchResult = await refetchTenants();
                const refreshedTenant = refetchResult.data?.find((eachTenant) => eachTenant.id === tSelectedTenantID);

                if (refreshedTenant) {
                    setTFormFromSelectedTenant(refreshedTenant);
                    setTSelectedTenantName(refreshedTenant.name);
                }

                showTResultModal(false, `${actionLabel} Tenant: ${tenantName} Succeeded.`);
            },
        }
    );

    const onFinishFailed = (_errorInfo: ManagedFormErrorInfo<AUDTenantFormValues>) => {
        showTInfoModal('Tenant Validation Error', 'Please correct the highlighted Tenant fields and try again.');
    };

    const onFinish = () => {
        audTenant();
    };

    const buildTenantExplainSelectionSummary = (selectedTenant: ManagedTenant | undefined): string => {
        if (!selectedTenant) {
            return '';
        }

        return [
            `Tenant ID: ${selectedTenant.id}`,
            `Tenant Name: ${selectedTenant.name}`,
            '',
            'The detailed Tenant Explain context is built by the backend.',
        ].join('\n');
    };

    const handleOpenAITenantExplainModal = () => {
        const selectedTenant = getSelectedTenant();

        setAIContextAssistCurrentText(buildTenantExplainSelectionSummary(selectedTenant));
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                'Unable to find an AI Provider matching Tenant Explain.',
            ]);
            return;
        }

        if (tSelectedTenantID <= 0) {
            setAIContextAssistAncillaryItems([
                'Select a Tenant before asking AI to explain it.',
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        getAIContextAssistPrompt({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            explainTenantID: tSelectedTenantID,
            prompt: requestText,
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };
    
    const getSelectedTenant = (selectedTenantID: number = tSelectedTenantID): ManagedTenant | undefined => {
        return tenants.find((tenant) => tenant.id === selectedTenantID);
    };

    const handleTSelectionChange = (value: number) => {
        setTSelectedTenantID(value);

        const selectedTenant = getSelectedTenant(value);

        setTSelectedTenantName(selectedTenant?.name ?? '');

        setTMode('add');
        setTBlankForm();
    };

    const setTBlankForm = () => {
        formAUDTenants.setFieldValue('tDetailsName', '');
        formAUDTenants.setFieldValue('tDetailsConnString', '');
        formAUDTenants.setFieldValue('tDetailsLogoImage', '');
        formAUDTenants.setFieldValue('tDetailsNameGeneric', '');
        formAUDTenants.setFieldValue('tDetailsHideFromDemo', false);
        formAUDTenants.setFieldValue('tDetailsSQLFirstDayOfWeek', DEFAULT_SQL_FIRST_DAY_OF_WEEK);
        formAUDTenants.setFieldValue('tDetailsApplicationID', '');
        formAUDTenants.setFieldValue('tDetailsLinkedDBName', '');
        formAUDTenants.setFieldValue('tDetailsURLBase', '');
        formAUDTenants.setFieldValue('tDetailsEncryptKey', '');
        formAUDTenants.setFieldValue('tDetailsEncryptCert', '');
    };

    const setTFormFromSelectedTenant = (selectedTenant: ManagedTenant | undefined = getSelectedTenant()) => {
        formAUDTenants.setFieldValue('tDetailsName', selectedTenant?.name ?? '');
        formAUDTenants.setFieldValue('tDetailsConnString', selectedTenant?.connString ?? '');
        formAUDTenants.setFieldValue('tDetailsLogoImage', selectedTenant?.logoImage ?? '');
        formAUDTenants.setFieldValue('tDetailsNameGeneric', selectedTenant?.nameGeneric ?? '');
        formAUDTenants.setFieldValue('tDetailsHideFromDemo', selectedTenant?.hideFromDemo ?? false);
        formAUDTenants.setFieldValue('tDetailsSQLFirstDayOfWeek', selectedTenant?.sqlFirstDayOfWeek ?? DEFAULT_SQL_FIRST_DAY_OF_WEEK);
        formAUDTenants.setFieldValue('tDetailsApplicationID', selectedTenant?.applicationId ?? '');
        formAUDTenants.setFieldValue('tDetailsLinkedDBName', selectedTenant?.linkedDBName ?? '');
        formAUDTenants.setFieldValue('tDetailsURLBase', selectedTenant?.urlBase ?? '');
        formAUDTenants.setFieldValue('tDetailsEncryptKey', selectedTenant?.encryptKey ?? '');
        formAUDTenants.setFieldValue('tDetailsEncryptCert', selectedTenant?.encryptCert ?? '');
    };

    const handleTTenantResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setTBlankForm();
            return;
        }

        setTFormFromSelectedTenant();
    };

    //=================================================================
    //=================================================================
    
    const handleTAddTenant = () => {
        resetAUDTenant();
        clearTSelection();
    };

    //=================================================================
    //=================================================================

    const handleTUpdTenant = () => {
        resetAUDTenant();
        setTMode('update');
        handleTTenantResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleTDelTenant = () => {
        resetAUDTenant();
        setTMode('delete');
        handleTTenantResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleManageModalOk = () => {
        setManageActionModalState((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const onTCancel = () => {
        resetAUDTenant();
        clearTSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '8px',
        height: '470px'
    };
    
    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageModalOk}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title='AI Tenant Explain'
                currentSectionTitle='Current Tenant Context'
                requestSectionTitle='What do you want explained?'
                resultSectionTitle='Tenant Explanation'
                ancillarySectionTitle='Ancillary Information'
                currentText={aiContextAssistCurrentText}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                requestPlaceholder='Optionally describe what part of this Tenant administration record you want explained.'
                askButtonText='Explain Tenant'
                useButtonText='Use Suggestion'
                loading={aiContextAssistLoading || isGetAIProvidersLoading}
                allowUseSuggestion={false}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={() => undefined}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <Spin spinning={
                            isGetTenantsLoading ||
                            isAUDTenantLoading
                           }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Tenants</Title>
                    </Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{ float: 'right' }}>Select Tenant to Update or Delete:</Text>
                    </Col>
                    <Col span={10}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Tenant'}
                            showSearch
                            style={{paddingTop: '4px', width: '100%'}}
                            value={tSelectedTenantID === -1 ? undefined : tSelectedTenantID}
                            onChange={handleTSelectionChange}
                        >
                            {tenants.map(
                                (eachTenant) => {
                                    return (
                                        <Option
                                            key={eachTenant.id}
                                            label={eachTenant.name}
                                            value={eachTenant.id}
                                        >
                                            {eachTenant.name}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                    <Col span={1}>
                        <Button
                            disabled={tSelectedTenantID === -1 || isGetAIProvidersLoading}
                            icon={<BulbOutlinedIlluminated />}
                            style={{
                                minWidth: '32px',
                                padding: '0px',
                                marginTop: '4px',
                            }}
                            title='AI Tenant Explain'
                            type='default'
                            onClick={handleOpenAITenantExplainModal}
                        />
                    </Col>
                    <Col span={3} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={3}>
                        <Button
                            icon={<AddIcon />}
                            style={{width: '95%', marginBottom: '4px', float: 'left',}}
                            type='primary'
                            onClick={handleTAddTenant}
                        >
                            Add Tenant
                        </Button>
                    </Col>
                    <Col span={9} />
                    <Col span={3}>
                        <Button
                            disabled={tSelectedTenantID === -1}
                            icon={<EditIcon />}
                            style={{width: '95%', backgroundColor: 'darkgray', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleTUpdTenant}
                        >
                            Update Tenant
                        </Button>
                    </Col>
                    <Col span={3}>
                        <Button
                            disabled={tSelectedTenantID === -1}
                            icon={<DeleteIcon />}
                            style={{width: '95%', backgroundColor: 'red', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleTDelTenant}
                        >
                            Delete Tenant
                        </Button>
                    </Col>
                    <Col span={4} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAUDTenants}
                    initialValues={{
                        tDetailsHideFromDemo: false,
                        tDetailsSQLFirstDayOfWeek: DEFAULT_SQL_FIRST_DAY_OF_WEEK,
                    }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div style={contentStyle}>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getTActionColor() }}>
                                            {addButtonClicked ? 'Add Tenant' :
                                                (updButtonClicked ? 'Update Tenant: [' + tSelectedTenantID + '] ' + tSelectedTenantName :
                                                    (delButtonClicked ? 'Delete Tenant: [' + tSelectedTenantID + '] ' + tSelectedTenantName : ''))}
                                        </Divider>
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button
                                            style={{ color: getTActionColor(), fontStyle: 'italic' }}
                                            type='link'
                                            onClick={() => handleTTenantResetForm(addButtonClicked ? 'blank' : 'selected')}
                                        >
                                            Reset Form
                                        </Button>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='tdetailsname'
                                            name='tDetailsName'
                                            label='Name'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Name required.',
                                                    required: true,
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='tdetailsnamegeneric'
                                            name='tDetailsNameGeneric'
                                            label='Name Generic'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Name Generic'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='tdetailslinkeddbname'
                                            name='tDetailsLinkedDBName'
                                            label='Linked DB Name'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Linked DB Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'left'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2}/>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Form.Item
                                            key='tdetailsconnstring'
                                            name='tDetailsConnString'
                                            label='Conn. String'
                                            labelCol={{ span: 3, }}
                                            rules={[
                                                {
                                                    message: 'Conn. String required.',
                                                    required: true,
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Conn. String'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='tdetailsurlbase'
                                            name='tDetailsURLBase'
                                            label='URL Base'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter URL Base'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'left'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='tdetailsencryptkey'
                                            name='tDetailsEncryptKey'
                                            label='Encrypt Key'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Encrypt Key'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='tdetailsencryptcert'
                                            name='tDetailsEncryptCert'
                                            label='Encrypt Cert'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Encrypt Cert'
                                                style={{color: delButtonClicked ? 'gray' : 'black', float: 'right'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='tdetailssqlfirstdayofweek'
                                            name='tDetailsSQLFirstDayOfWeek'
                                            label='SQL 1st Day Of Week'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'SQL 1st Day Of Week required.',
                                                    required: true,
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Select
                                                allowClear={false}
                                                disabled={delButtonClicked}
                                                options={sqlFirstDayOfWeekOptions}
                                                placeholder="Select SQL 1st Day Of Week"
                                                style={{ width: '100%' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='tdetailslogoimage'
                                            name='tDetailsLogoImage'
                                            label='Logo Image'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Logo Image'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={7}>
                                        <Form.Item
                                            key='tdetailsapplicationid'
                                            name='tDetailsApplicationID'
                                            label='Application ID'
                                            labelCol={{ span: 9, }}
                                            rules={[
                                                {
                                                    message: 'Application ID must be a valid GUID or blank.',
                                                    validator: validateTDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Application ID'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ visibility: addButtonClicked ? 'hidden' : 'visible' }}>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                                            <Text italic style={{ color: 'gray' }}>
                                                NOTE: Tenants may be linked (via Foreign Key) to multiple other tables. Report Items, Report Securities, DataSet Securities, Tenant User Maps, and more.
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={17} />
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='tApplyButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType='submit'
                                        style={{width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black'))}}
                                        type='primary'
                                    >
                                        {addButtonClicked ? 'Add Tenant' : (updButtonClicked ? 'Update Tenant' : (delButtonClicked ? 'Delete Tenant' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='tCancelButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        style={{width: '95%'}}
                                        type='default'
                                        onClick={onTCancel}
                                    >
                                        {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                </Form>
            </Spin>
        </Card>
    );
};

export default AUDTenant;
